/* hs-eslint ignored failing-rules */
/* eslint-disable hubspot-dev/no-private-classes */

'use es6';

import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import I18n from 'I18n';
import { getIsAdvancedPageTemplateWithoutAccess } from 'ContentEditorUI/redux/selectors/templateInfoSelectors';
import WillUnpublishSoonWarning from '../components/WillUnpublishSoonWarning';
import JitadAndCantSaveWarning from '../components/JitadAndCantSaveWarning';
import ReadOnlyTestVariantWarning from '../components/ReadOnlyTestVariantWarning';
import { getUnpublishWarningLangKey } from 'ContentEditorUI/redux/selectors/publishSelectors';
import LimitExceededError from '../components/LimitExceededError';
import AsyncComponentContext from 'ContentEditorUI/context/AsyncComponentContext';
import { getHasContentTypeLimitReducerAndExceededLimit } from 'ContentEditorUI/redux/selectors/limitSelectors';
import { getShouldShowSmartRulePreviewToolbar } from 'ContentEditorUI/redux/selectors/smartViewSelectors';
import DowngradeAlert from 'page-editor-ui/components/DowngradeAlert';
import UIUpgradeBanner from 'ui-addon-upgrades/ums/UIUpgradeBanner';
import { getIsDynamicPage, getIsPrivatePage } from '../redux/selectors/pageContentModelSelectors';
import { getDynamicPageAccessPurgeDateRequest, getSitePagesPasswordProtectionPurgeDateRequest, getLandingPagesPasswordProtectionPurgeDateRequest, getAudienceAccessPurgeDateRequest } from 'ContentEditorUI/redux/selectors/requestStatesSelectors';
import { requestUninitialized } from 'redux-request-state/util/requestStateCheckers';
import { fetchDynamicPageAccessPurgeDate, fetchSitePagesPasswordProtectionPurgeDate, fetchLandingPagesPasswordProtectionPurgeDate, fetchAudienceAccessPurgeDate } from 'ContentEditorUI/redux/actions/portalActions';
import { getSitePagesPasswordProtectionPurgeDate, getLandingPagesPasswordProtectionPurgeDate, getAudienceAccessPurgeDate, getDynamicPageAccessPurgeDate } from 'ContentEditorUI/redux/selectors/portalSelectors';
import { getIsBlogListingPage, getIsLandingPage, getIsSitePage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { getShouldShowDynamicPageDowngradeBanner, getShouldShowSitePagesPrivatePageDowngradeBanner, getShouldShowLandingPagesPrivatePageDowngradeBanner, getShouldShowAudienceAccessPageDowngradeBanner, getShouldShowRemoveBrandingMessage } from '../redux/selectors/downgradeSelectors';
import { getHasInvalidModuleBodyError } from 'ContentEditorUI/redux/selectors/moduleErrorSelectors';
import { getIsUserJitad, getIsUngatedForPatagonia } from 'ContentEditorUI/redux/selectors/authSelectors';
import { getDisableCustomJitaUI } from 'page-editor-ui/redux/selectors/testFlagSelectors';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AnnouncementBanner from 'ContentEditorUI/components/AnnouncementBanner';
import InvalidModuleBodyWarning from 'ContentEditorUI/components/temp/InvalidModuleBodyWarning';
import { COLLAPSED_SIDEBAR_WIDTH } from 'collaboration-sidebar';
const getDateAsMMDDYYYY = ts => I18n.moment.portalTz(ts).format('l');
import { getIsABVariantWithoutAccess } from 'ContentEditorUI/redux/selectors/abTestSelectors';
import { getIsMabVariantWithoutAccess } from 'ContentEditorUI/redux/selectors/mabExperimentSelectors';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
const SIDEBAR_WIDTH = '380';
const FEB_09_2024 = 1707454800000; // Fri Feb 09 2024 00:00:00 GMT-0500 (Eastern Standard Time)
const FEB_16_2024 = 1708059600000; // Fri Feb 16 2024 00:00:00 GMT-0500 (Eastern Standard Time)
const FEB_23_2024 = 1708664400000; // Fri Feb 23 2024 00:00:00 GMT-0500 (Eastern Standard Time)

const MessagingWrapper = styled.div.withConfig({
  displayName: "PageIframeMessagingAreaContainer__MessagingWrapper",
  componentId: "sc-1gar3ag-0"
})(["min-width:100%;width:calc(100vw - ", "px - ", "px);"], SIDEBAR_WIDTH, COLLAPSED_SIDEBAR_WIDTH);
const PageIframeMessagingAreaContainer = () => {
  const dispatch = useDispatch();
  const isAdvancedTemplateWithoutAccess = useSelector(getIsAdvancedPageTemplateWithoutAccess);
  const unpublishWarningLangKey = useSelector(getUnpublishWarningLangKey);
  const hasExceededLimit = useSelector(getHasContentTypeLimitReducerAndExceededLimit);
  const shouldShowSmartRulePreviewToolbar = useSelector(getShouldShowSmartRulePreviewToolbar);
  const isBlogListingPage = useSelector(getIsBlogListingPage);
  const isLandingPage = useSelector(getIsLandingPage);
  const isSitePage = useSelector(getIsSitePage);
  const sitePagesPasswordProtectionPurgeDateRequest = useSelector(getSitePagesPasswordProtectionPurgeDateRequest);
  const landingPagesPasswordProtectionPurgeDateRequest = useSelector(getLandingPagesPasswordProtectionPurgeDateRequest);
  const audienceAccessPurgeDateRequest = useSelector(getAudienceAccessPurgeDateRequest);
  const dynamicPageAccessPurgeDateRequest = useSelector(getDynamicPageAccessPurgeDateRequest);
  const sitePagesPasswordProtectionPurgeDate = useSelector(getSitePagesPasswordProtectionPurgeDate);
  const landingPagesPasswordProtectionPurgeDate = useSelector(getLandingPagesPasswordProtectionPurgeDate);
  const audienceAccessPurgeDate = useSelector(getAudienceAccessPurgeDate);
  const dynamicPageAccessPurgeDate = useSelector(getDynamicPageAccessPurgeDate);
  const shouldShowSitePagesPrivatePageDowngradeBanner = useSelector(getShouldShowSitePagesPrivatePageDowngradeBanner);
  const shouldShowLandingPagesPrivatePageDowngradeBanner = useSelector(getShouldShowLandingPagesPrivatePageDowngradeBanner);
  const shouldShowAudienceAccessPageDowngradeBanner = useSelector(getShouldShowAudienceAccessPageDowngradeBanner);
  const shouldShowDynamicPageDowngradeBanner = useSelector(getShouldShowDynamicPageDowngradeBanner);
  const shouldShowRemoveBrandingMessage = useSelector(getShouldShowRemoveBrandingMessage);
  const pageHasInvalidModuleBodyError = useSelector(getHasInvalidModuleBodyError);
  const isDynamicPage = useSelector(getIsDynamicPage);
  const isPrivatePage = useSelector(getIsPrivatePage);
  const isUserJitad = useSelector(getIsUserJitad);
  const disableCustomJitaUI = useSelector(getDisableCustomJitaUI);
  const isUngatedForPatagonia = useSelector(getIsUngatedForPatagonia);
  useEffect(() => {
    if (isPrivatePage) {
      if (isSitePage) {
        if (requestUninitialized(sitePagesPasswordProtectionPurgeDateRequest)) {
          dispatch(fetchSitePagesPasswordProtectionPurgeDate());
        }
      } else if (requestUninitialized(landingPagesPasswordProtectionPurgeDateRequest)) {
        dispatch(fetchLandingPagesPasswordProtectionPurgeDate());
      }
      if (requestUninitialized(audienceAccessPurgeDateRequest)) {
        dispatch(fetchAudienceAccessPurgeDate());
      }
    }
    if (isSitePage && isDynamicPage && requestUninitialized(dynamicPageAccessPurgeDateRequest)) {
      dispatch(fetchDynamicPageAccessPurgeDate());
    }
  }, [dispatch, isSitePage, isDynamicPage, isPrivatePage, sitePagesPasswordProtectionPurgeDateRequest, landingPagesPasswordProtectionPurgeDateRequest, audienceAccessPurgeDateRequest, dynamicPageAccessPurgeDateRequest]);
  const isABVariantWithoutAccess = useSelector(getIsABVariantWithoutAccess);
  const isMabVariantWithoutAccess = useSelector(getIsMabVariantWithoutAccess);
  const memoizedMessageNodes = useMemo(() => {
    const messageNodes = [];
    if (isAdvancedTemplateWithoutAccess) {
      if (isUserJitad && !disableCustomJitaUI) {
        messageNodes.push( /*#__PURE__*/_jsx(JitadAndCantSaveWarning, {}, "jitad-and-cant-save-warning"));
      } else if (isLandingPage) {
        messageNodes.push( /*#__PURE__*/_jsx(WillUnpublishSoonWarning, {
          langKey: unpublishWarningLangKey
        }, "will-unpublish-soon-warning"));
      }
    }
    if (hasExceededLimit) {
      messageNodes.push( /*#__PURE__*/_jsx(LimitExceededError, {}, "limit-exceeded-error"));
    }
    if (isABVariantWithoutAccess || isMabVariantWithoutAccess) {
      messageNodes.push( /*#__PURE__*/_jsx(ReadOnlyTestVariantWarning, {
        isAbTest: isABVariantWithoutAccess,
        isMabTest: isMabVariantWithoutAccess,
        isUserJitad: isUserJitad
      }, "test-variant-downgrade-banner"));
    }
    if (shouldShowRemoveBrandingMessage) {
      if (isLandingPage) {
        messageNodes.push( /*#__PURE__*/_jsx(UIUpgradeBanner, {
          upgradeKey: "landing-page-editor-remove-branding-preview-frame-top"
        }));
      } else if (isBlogListingPage) {
        messageNodes.push( /*#__PURE__*/_jsx(UIUpgradeBanner, {
          upgradeKey: "blog-listing-page-editor-remove-branding-preview-frame-top"
        }));
      } else if (isSitePage) {
        messageNodes.push( /*#__PURE__*/_jsx(UIUpgradeBanner, {
          upgradeKey: "page-editor-remove-branding-preview-frame-limit-increase-top"
        }));
      }
    }
    if (shouldShowAudienceAccessPageDowngradeBanner) {
      messageNodes.push( /*#__PURE__*/_jsx(DowngradeAlert, {
        downgradeMessageKey: "audienceAccessPage",
        options: {
          unpublishDate: getDateAsMMDDYYYY(audienceAccessPurgeDate)
        }
      }, "audience-access-page-downgrade-banner"));
    }
    if (shouldShowSitePagesPrivatePageDowngradeBanner || shouldShowLandingPagesPrivatePageDowngradeBanner) {
      messageNodes.push( /*#__PURE__*/_jsx(DowngradeAlert, {
        downgradeMessageKey: "privatePage",
        options: {
          unpublishDate: getDateAsMMDDYYYY(shouldShowSitePagesPrivatePageDowngradeBanner ? sitePagesPasswordProtectionPurgeDate : landingPagesPasswordProtectionPurgeDate)
        }
      }, "private-page-downgrade-banner"));
    }
    if (shouldShowDynamicPageDowngradeBanner) {
      messageNodes.push( /*#__PURE__*/_jsx(DowngradeAlert, {
        downgradeMessageKey: "dynamicPage",
        options: {
          unpublishDate: getDateAsMMDDYYYY(dynamicPageAccessPurgeDate)
        }
      }, "dynamic-page-downgrade-banner"));
    }
    if (shouldShowSmartRulePreviewToolbar) {
      messageNodes.push( /*#__PURE__*/_jsx(AsyncComponentContext.Consumer, {
        children: ({
          SmartRulePreviewToolbar
        }) => /*#__PURE__*/_jsx(SmartRulePreviewToolbar, {}, "smart-rule-preview-toolbar")
      }));
    }
    if (!isUngatedForPatagonia) {
      messageNodes.push( /*#__PURE__*/_jsx(AnnouncementBanner, {
        bannerKey: "patagonia",
        consumerName: "page-editor-ui",
        shouldPersistSeenState: true,
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "announcementBanner.patagonia.title"
        }),
        alertType: "danger",
        showAgainAfterDates: [FEB_09_2024, FEB_16_2024, FEB_23_2024],
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "announcementBanner.patagonia.body_jsx",
          elements: {
            UILink: Link
          }
        })
      }, "coming-soon-banner-patagonia"));
    }
    if (pageHasInvalidModuleBodyError) {
      messageNodes.push( /*#__PURE__*/_jsx(InvalidModuleBodyWarning, {}));
    }
    return messageNodes;
  }, [audienceAccessPurgeDate, disableCustomJitaUI, dynamicPageAccessPurgeDate, hasExceededLimit, isABVariantWithoutAccess, isAdvancedTemplateWithoutAccess, isBlogListingPage, isLandingPage, isMabVariantWithoutAccess, isSitePage, isUngatedForPatagonia, isUserJitad, landingPagesPasswordProtectionPurgeDate, shouldShowAudienceAccessPageDowngradeBanner, shouldShowDynamicPageDowngradeBanner, shouldShowLandingPagesPrivatePageDowngradeBanner, shouldShowRemoveBrandingMessage, shouldShowSitePagesPrivatePageDowngradeBanner, shouldShowSmartRulePreviewToolbar, sitePagesPasswordProtectionPurgeDate, unpublishWarningLangKey, pageHasInvalidModuleBodyError]);
  if (memoizedMessageNodes.length) {
    return /*#__PURE__*/_jsx(MessagingWrapper, {
      "data-test-id": "contentEditor-iframe-messaging-area",
      children: memoizedMessageNodes
    });
  }
  return null;
};
export default PageIframeMessagingAreaContainer;